import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Check, RefreshCw, Package } from 'lucide-react';
import { Grn, GrnItem } from '../../types/grn';
import { JobCard } from '../../types/jobcard';
import { getJobCard } from '../../services/jobcardApi';
import { createGrn, updateGrn, getPoItems, finalizeGrn } from '../../services/grnApi';
import { showSuccessToast } from '../../utils/toast';
import { showErrorToast } from '../../utils/toast';

interface GrnFormProps {
  jobCardId: number;
  grn?: Grn | null;
  onSave: (grn: Grn) => void;
  onClose: () => void;
  isEditing?: boolean;
}

export default function GrnForm({ jobCardId, grn, onSave, onClose, isEditing = false }: GrnFormProps) {
  const [jobCard, setJobCard] = useState<JobCard | null>(null);
  const [formData, setFormData] = useState({
    received_date: grn?.received_date || new Date().toISOString().split('T')[0],
    received_time: grn?.received_time || new Date().toTimeString().slice(0, 5),
    received_by: (grn as Grn & { received_by?: string })?.received_by || '',
    notes: grn?.notes || '',
  });

  const [items, setItems] = useState<Partial<GrnItem & { original_ordered_qty?: number; previously_received_qty?: number }>[]>(
    grn?.items?.length ? grn.items : [
      {
        item_code: '',
        description: '',
        ordered_qty: 0,
        received_qty: 0,
        unit_price: 0,
        vatable: true,
        line_total: 0,
      }
    ]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPoItems, setIsLoadingPoItems] = useState(false);
  const [hasLoadedPoItems, setHasLoadedPoItems] = useState(false);

  const loadPoItems = useCallback(async () => {
    setIsLoadingPoItems(true);
    try {
      const items = await getPoItems(jobCardId);
      setHasLoadedPoItems(true);
      
      // Filter items that can still be received and auto-populate the table
      const receivableItems = items.filter(item => item.can_receive);
      
      if (receivableItems.length > 0) {
        const grnItems: Partial<GrnItem>[] = receivableItems.map(item => ({
          po_line_id: item.id,
          item_code: item.item_code || '',
          description: item.description || '',
          ordered_qty: parseInt(item.remaining_qty) || 0, // Use remaining_qty as the max allowed
          received_qty: parseInt(item.remaining_qty) || 0, // Start with remaining quantity
          unit_price: Number(item.unit_price) || 0,
          // Preserve VAT flag exactly as provided by backend; coerce to boolean without defaulting to true
          vatable: Boolean(item.vatable),
          line_total: (parseInt(item.remaining_qty) || 0) * (Number(item.unit_price) || 0),
          // Store original quantities for display
          original_ordered_qty: parseInt(item.ordered_qty) || 0,
          previously_received_qty: parseInt(item.received_qty) || 0,
        }));
        setItems(grnItems);
      } else {
        // No items can be received - all are fully received
        setItems([]);
      }
    } catch (error) {
      console.error('Error loading PO items:', error);
      setHasLoadedPoItems(false);
    } finally {
      setIsLoadingPoItems(false);
    }
  }, [jobCardId]);

  // Load PO items when component mounts (only for new GRNs)
  useEffect(() => {
    if (!isEditing) {
      loadPoItems();
    }
  }, [isEditing, loadPoItems]);

  // Load job card details to display order number in header
  useEffect(() => {
    let isMounted = true;
    const hasNestedData = (r: unknown): r is { data: { data: JobCard } } => {
      if (typeof r !== 'object' || r === null) return false;
      const outer = r as { data?: unknown };
      if (typeof outer.data !== 'object' || outer.data === null) return false;
      const inner = outer.data as { data?: unknown };
      return typeof inner.data === 'object' && inner.data !== null;
    };
    (async () => {
      try {
        const response = await getJobCard(jobCardId);
        let extracted: JobCard | null = null;
        if (hasNestedData(response)) {
          extracted = response.data.data;
        } else if (typeof response === 'object' && response !== null) {
          const maybe = response as { data?: unknown };
          if (maybe.data && typeof maybe.data === 'object') {
            extracted = maybe.data as JobCard;
          }
        }
        if (isMounted) {
          setJobCard(extracted);
        }
      } catch {
        // Silently ignore; header will simply not show order number
        if (isMounted) {
          setJobCard(null);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [jobCardId]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);


  const addItem = () => {
    setItems([...items, {
      item_code: '',
      description: '',
      ordered_qty: 0,
      received_qty: 0,
      unit_price: 0,
      // Default manual item VAT to false; user can toggle as needed
      vatable: false,
      line_total: 0,
    }]);
  };


  const updateItem = (index: number, field: keyof GrnItem, value: unknown) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate line total
    if (field === 'received_qty' || field === 'unit_price') {
      const receivedQty = field === 'received_qty' ? parseInt(String(value)) || 0 : parseInt(String(newItems[index].received_qty)) || 0;
      const unitPrice = field === 'unit_price' ? Number(value) : Number(newItems[index].unit_price) || 0;
      newItems[index].line_total = receivedQty * unitPrice;
    }
    
    setItems(newItems);
  };

  const validateReceivedQuantity = (index: number, value: number): boolean => {
    const item = items[index];
    const orderedQty = item.ordered_qty || 0;
    return value <= orderedQty && value >= 0;
  };

  const calculateTotals = () => {
    const vatRate = 0.15; // 15% VAT rate
    let netTotal = 0;
    let vatTotal = 0;

    items.forEach(item => {
      const lineTotal = Number(item.line_total) || 0;
      netTotal += lineTotal;
      if (item.vatable) {
        vatTotal += lineTotal * vatRate;
      }
    });

    return {
      net: Number(netTotal.toFixed(2)),
      vat: Number(vatTotal.toFixed(2)),
      gross: Number((netTotal + vatTotal).toFixed(2)),
    };
  };

  const handleSubmit = async (e: React.FormEvent, finalize: boolean = true) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate all received quantities
      const invalidItems = items.filter((item, index) => 
        !validateReceivedQuantity(index, item.received_qty || 0)
      );
      
      if (invalidItems.length > 0) {
        showErrorToast('Please fix invalid quantities before submitting');
        setIsSubmitting(false);
        return;
      }

      const totals = calculateTotals();
      const payload = {
        ...formData,
        items: items.map(item => ({
          ...item,
          po_line_id: item.po_line_id || null,
          item_code: item.item_code || '',
          description: item.description || '',
          ordered_qty: item.ordered_qty || 0,
          received_qty: item.received_qty || 0,
          unit_price: item.unit_price || 0,
          vatable: item.vatable || false,
          line_total: item.line_total || 0,
        })) as GrnItem[],
        gross_total: totals.gross,
        vat_total: totals.vat,
        net_total: totals.net,
      };

      let savedGrn: Grn;
      if (isEditing && grn?.id) {
        savedGrn = await updateGrn(grn.id, payload);
      } else {
        savedGrn = await createGrn(jobCardId, payload);
      }

      // Only finalize if requested
      if (finalize && savedGrn.id) {
        const finalizedGrn = await finalizeGrn(savedGrn.id);
        onSave(finalizedGrn);
        showSuccessToast('GRN saved and finalized successfully');
      } else {
        onSave(savedGrn);
        showSuccessToast('GRN saved as draft');
      }
    } catch (error) {
      console.error('Error saving GRN:', error);
      // TODO: Show error toast
    } finally {
      setIsSubmitting(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEditing ? 'Edit GRN Receipt' : 'Confirm Received Items'}{jobCard?.order_number ? ` · PO #${jobCard.order_number}` : ''}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Receipt Confirmation Info */}

        {/* Items */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-medium text-gray-900 dark:text-white"></h4>
            <div className="flex space-x-2">
              {!isEditing && (
                <button
                  type="button"
                  onClick={loadPoItems}
                  disabled={isLoadingPoItems}
                  className="inline-flex items-center px-3 py-1.5 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${isLoadingPoItems ? 'animate-spin' : ''}`} />
                  Refresh PO Items
                </button>
              )}
              <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors duration-200"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Manual Item
              </button>
            </div>
          </div>

          {/* PO Items Loading State */}
          {isLoadingPoItems && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-sm text-blue-700 dark:text-blue-300">Loading PO items...</span>
              </div>
            </div>
          )}

          {/* PO Items Loaded Info */}
          {!isLoadingPoItems && !isEditing && items.length > 0 && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
              <div className="flex items-center">
                <Check className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                <span className="text-sm text-green-700 dark:text-green-300">
                  Successfully loaded {items.length} items with remaining quantities from purchase order. Adjust received quantities below.
                </span>
              </div>
            </div>
          )}

          {/* All Items Fully Received Info */}
          {!isLoadingPoItems && !isEditing && hasLoadedPoItems && items.length === 0 && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
              <div className="flex items-center">
                <Check className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                <span className="text-sm text-blue-700 dark:text-blue-300">
                  All items from the purchase order have been fully received. No additional GRN needed.
                </span>
              </div>
            </div>
          )}

          {/* PO Items Table - Only show when items are loaded */}
          {!isLoadingPoItems && items.length > 0 && (
            <div className="mb-6">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                {/* Fixed Header */}
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Confirm Received Quantities ({items.length} items with remaining quantities)
                  </h4>
                </div>
                
                {/* Scrollable Table Body */}
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Item Code
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Ordered Qty
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Unit Price
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          VAT
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Received Qty
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Line Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                      {items.map((item, index) => {
                        const isManualItem = !item.po_line_id; // Manual items don't have po_line_id
                        return (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono">
                              {isManualItem ? (
                                <input
                                  type="text"
                                  value={item.item_code || ''}
                                  onChange={(e) => updateItem(index, 'item_code', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono"
                                  placeholder="Enter item code"
                                />
                              ) : (
                                item.item_code || '-'
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-xs">
                              {isManualItem ? (
                                <input
                                  type="text"
                                  value={item.description || ''}
                                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                  placeholder="Enter description"
                                />
                              ) : (
                                <span className="truncate">{item.description || '-'}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-right">
                              {isManualItem ? (
                                <input
                                  type="number"
                                  step="1"
                                  min="0"
                                  value={item.ordered_qty || ''}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value) || 0;
                                    updateItem(index, 'ordered_qty', value);
                                  }}
                                  className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-right"
                                  placeholder="0"
                                />
                              ) : (
                                (item as GrnItem & { original_ordered_qty?: number }).original_ordered_qty || item.ordered_qty || 0
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-right">
                              {isManualItem ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={item.unit_price || ''}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    updateItem(index, 'unit_price', value);
                                  }}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-right"
                                  placeholder="0.00"
                                />
                              ) : (
                                `R ${(Number(item.unit_price) || 0).toFixed(2)}`
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-center">
                              <button
                                type="button"
                                onClick={() => updateItem(index, 'vatable', !item.vatable)}
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                  item.vatable 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50' 
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                              >
                                {item.vatable ? 'Yes' : 'No'}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  step="1"
                                  min="0"
                                  max={item.ordered_qty || 0}
                                  value={item.received_qty || ''}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value) || 0;
                                    if (validateReceivedQuantity(index, value)) {
                                      updateItem(index, 'received_qty', value);
                                    }
                                  }}
                                  className={`w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-right ${
                                    !validateReceivedQuantity(index, item.received_qty || 0) 
                                      ? 'border-red-500 dark:border-red-400' 
                                      : 'border-gray-300 dark:border-gray-600'
                                  }`}
                                  placeholder="0"
                                />
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  / {item.ordered_qty || 0} remaining
                                  {!isManualItem && ((item as GrnItem & { previously_received_qty?: number }).previously_received_qty || 0) > 0 && (
                                    <span className="text-orange-600 dark:text-orange-400 ml-1">
                                      ({item.ordered_qty || 0} of {(item as GrnItem & { original_ordered_qty?: number }).original_ordered_qty || 0} ordered)
                                    </span>
                                  )}
                                </span>
                              </div>
                              {!validateReceivedQuantity(index, item.received_qty || 0) && (
                                <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                                  Cannot exceed remaining quantity
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right font-medium">
                              R {(Number(item.line_total) || 0).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Fixed Footer with Stats */}
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Total Items:</span> {items.length} | 
                      <span className="font-medium ml-2">Quantities Received:</span> {items.reduce((sum, item) => sum + (parseInt(String(item.received_qty)) || 0), 0)} | 
                      <span className="font-medium ml-2">Quantities Remaining:</span> {items.reduce((sum, item) => sum + (parseInt(String(item.ordered_qty)) || 0), 0)}
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      <span className="text-gray-600 dark:text-gray-400">Net Total:</span> R {(items.reduce((sum, item) => sum + (Number(item.line_total) || 0), 0)).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No PO Items Found Info - Only show if we haven't loaded PO items or they failed to load */}
          {!isLoadingPoItems && !isEditing && !hasLoadedPoItems && items.length === 0 && (
            <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg">
              <div className="flex items-center">
                <Check className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                <span className="text-sm text-yellow-700 dark:text-yellow-300">
                  No PO items found. You can manually add items below.
                </span>
              </div>
            </div>
          )}

          {/* Manual Items Section - Only show if no PO items loaded */}
          {items.length === 0 && !isLoadingPoItems && (
            <div className="space-y-4">
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p>No items loaded. PO items will be automatically loaded, or you can add manual items below.</p>
              </div>
            </div>
          )}
        </div>

        {/* Receipt Details - Compact Form */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Receipt Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Date Received
              </label>
              <input
                type="date"
                value={formData.received_date}
                onChange={(e) => setFormData({ ...formData, received_date: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Time Received
              </label>
              <input
                type="time"
                value={formData.received_time}
                onChange={(e) => setFormData({ ...formData, received_time: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Received By
              </label>
              <input
                type="text"
                value={formData.received_by}
                onChange={(e) => setFormData({ ...formData, received_by: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
                placeholder="Enter name of person who received..."
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Notes (Optional)
              </label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
                placeholder="Any additional notes..."
              />
            </div>
          </div>
        </div>

        {/* Totals Summary */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Summary</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Net Total:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">R {(totals.net || 0).toFixed(2)}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">VAT (15%):</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">R {(totals.vat || 0).toFixed(2)}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Total:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">R {(totals.gross || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-200"
          >
            Cancel
          </button>
          
          {/* Save Draft Button - only show for new GRNs or if editing a draft */}
          {(!isEditing || grn?.status === 'draft') && (
            <button
              type="button"
              onClick={(e) => handleSubmit(e, false)}
              disabled={isSubmitting || items.length === 0}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Package className="w-4 h-4 mr-2" />
                  Save Draft
                </>
              )}
            </button>
          )}
          
          <button
            type="submit"
            disabled={isSubmitting || items.length === 0}
            className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                {isEditing ? 'Update & Finalize' : 'Save & Finalize'}
              </>
            )}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
